import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';

// Design
import { Grid } from '@material-ui/core';
import { useFormHook } from 'design-system';
// Data Layer
import { ActiveConfig, GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';

// Analytics
import { AUTO_INFO_STEP, trackEvent } from 'src/analytics';

// Functions
import filterGeneralCatalog from 'src/functions/filterGeneralCatalog';
import mapToCatalogItem from 'src/functions/mapToCatalogItem';

// Models
import ModelsCarItem from 'src/models/Catalogs/ModelsCarItem';
import CarItemFromLicense from 'src/models/Quotation/CarItemFromLicense';
import CarItem from 'src/models/helperTypes/CarItem';
import CatalogItem from 'src/models/helperTypes/CatalogItem';
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';

// Constants
import { REQUEST_INSURED_VALUES } from 'src/store/car/requestIdentifiers';

// Redux
import { updateGeneralData } from 'src/store/car/actions';
import { getModels } from 'src/store/catalogs/actions';
import { REQUEST_MODELS } from 'src/store/catalogs/requestIdentifiers';
import {
  cleanCarDetailsFromLicense,
  getCarDetailsFromLicense,
} from 'src/store/quotation/actions';
import { REQUEST_LICENSEPLATE } from 'src/store/quotation/requestIdentifiers';
import { RootState } from 'src/store/rootReducer';

// Component
import {
  ButtonSection,
  CarDataSection,
  InformationSection,
  LicensePlateSection,
} from './components';
import {
  CarDetailsFormData,
  createInitialValues,
  generateNewCar,
} from './formLogicHelper';
import { carStateConfigValue, selectedModelFound } from './helperFunctions';
import makeValidationSchema from './makeValidationSchema';

export const CarDetailsForm = ({
  // Extra
  onSubmit,
  loading,
  // Redux state
  carItemFromLicense,
  brands,
  models,
  car,
  activeConfigs,
  setCarAction,
  getCarDetailsFromLicenseAction,
  getModelsAction,
  currentProfile,
  cleanCarDetailAction,
}: Props): JSX.Element => {
  const [groupID, setGroupID] = useState(car.model.groupId);
  const carYearConfig = activeConfigs['car-year'];
  const modelsConfig = activeConfigs['allowed-models'];
  const requestInsuredValues = useNetworkObserver(REQUEST_INSURED_VALUES);
  const requestModels = useNetworkObserver(REQUEST_MODELS);
  const requestLicensePlate = useNetworkObserver(REQUEST_LICENSEPLATE);
  const [disabled, setDisabled] = useState(true);

  const modelsList = useMemo(() => {
    const filteredModels = filterGeneralCatalog(models, modelsConfig?.modelsId);
    return mapToCatalogItem(filteredModels);
  }, [models, modelsConfig]);

  const submitData = (data: CarDetailsFormData): void => {
    const newCar = generateNewCar(
      carItemFromLicense,
      data,
      groupID,
      models,
      brands,
    );
    setCarAction(newCar);
    trackEvent({ eventName: AUTO_INFO_STEP, eventProperties: {} });
    onSubmit(newCar);
  };

  const { getInputProps, updateMultipleInputs, handleSubmit } =
    useFormHook<CarDetailsFormData>(
      createInitialValues(car, currentProfile),
      submitData,
      makeValidationSchema(currentProfile),
      {
        inputDependencies: {
          isNewCar: ['carYear'],
          carValue: ['hasInsuredRange', 'maxLimit', 'minLimit'],
        },
        deferErrorCheck: true,
      },
    );

  const handleLicenseError = useCallback(() => {
    updateMultipleInputs({
      carYear: '',
      carModel: '',
      passengersQty: '',
      noLicensePlate: false,
      fuel: '',
      color: '',
    });
    cleanCarDetailAction();
  }, [updateMultipleInputs, cleanCarDetailAction]);

  const onChangeModel = useCallback(
    (val: CatalogItem) => {
      const { groupId } = val?.Object as { groupId: string | undefined };
      setGroupID(groupId);
      updateMultipleInputs({
        carModel: val.Value,
      });
    },
    [updateMultipleInputs],
  );

  // Insured Car Logic
  useEffect(() => {
    if (carYearConfig) {
      getInputProps('isNewCar').value = carStateConfigValue(
        carYearConfig.category,
      );
    }
  }, [carYearConfig, getInputProps]);

  /** There can be a situation where a model does not appear on a Brand
   *  Honda CRV quotation, and then backing to change it to Honda Otros*
   *  We set the value to empty if it does not exists on the render condition list.
   */
  useEffect(() => {
    const inputModel = getInputProps('carModel').value as string;
    if (
      !selectedModelFound(modelsList, inputModel) &&
      requestModels === 'success'
    ) {
      getInputProps('carModel').value = '';
    }
    if (modelsList.length === 1) {
      getInputProps('carModel').value = modelsList[0].Value;
    }
  }, [getInputProps, modelsList, requestModels]);

  useEffect(() => {
    if (requestLicensePlate === 'success') {
      const carBrand = brands.find(
        (brand) => brand.name === carItemFromLicense.data.brand,
      );
      if (carBrand) {
        updateMultipleInputs({
          carYear: carItemFromLicense.data.yearManufacture,
          carBrand: carBrand?.code,
          passengersQty: carItemFromLicense.data.capacity,
          fuel: carItemFromLicense.data.fuel,
          color: carItemFromLicense.data.color,
        });
        getModelsAction(carBrand.id);
      } else {
        handleLicenseError();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestLicensePlate]);

  useEffect(() => {
    const noLicensePlate = getInputProps('noLicensePlate').value;
    if (
      requestModels === 'success' &&
      !noLicensePlate &&
      requestLicensePlate === 'success'
    ) {
      const carModel = modelsList.find(
        (model) => model.Text === carItemFromLicense.data.style,
      );
      if (carModel) {
        onChangeModel(carModel);
      } else {
        handleLicenseError();
      }
    }
    // eslint-disable-next-line
  }, [requestModels]);

  const handleCarDetailsFormSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    handleSubmit();
  };

  return (
    <form data-testid="car-details-form" onSubmit={handleCarDetailsFormSubmit}>
      <Grid container justifyContent="center" spacing={2}>
        <LicensePlateSection
          data-testid="license-plate-section"
          getInputProps={getInputProps}
          updateMultipleInputs={updateMultipleInputs}
          cleanCarDetailsFromLicense={cleanCarDetailAction}
          getCarDetailsFromLicenseAction={getCarDetailsFromLicenseAction}
          setDisabled={setDisabled}
        />
        <CarDataSection
          data-testid="car-data-section"
          getInputProps={getInputProps}
          updateMultipleInputs={updateMultipleInputs}
          setGroupID={(value: string | null | undefined) => setGroupID(value)}
          onChangeModel={onChangeModel}
          modelsList={modelsList}
          groupID={groupID}
          carYearConfig={carYearConfig}
          requestingModels={requestModels === 'requesting'}
          isCollectiveProfile={currentProfile.type === 'colectiva'}
          disabled={disabled}
        />
        <InformationSection data-testid="information-section" />
        <ButtonSection
          data-testid="button-section"
          loading={loading}
          disabled={requestInsuredValues === 'requesting'}
        />
      </Grid>
    </form>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  models: ModelsCarItem[];
  car: CarItem;
  carItemFromLicense: CarItemFromLicense;
  activeConfigs: ActiveConfig;
  carTypesOfUse: GeneralCatalogItem[];
  brands: GeneralCatalogItem[];
  currentProfile: PricingProfileResponse;
  copyingQuotation: boolean;
} => ({
  brands: state.catalog.brands,
  carTypesOfUse: state.catalog.carTypesOfUse,
  models: state.catalog.models,
  carItemFromLicense: state.quotation.carItemFromLicense,
  car: state.car.currentCar,
  activeConfigs: state.dynamicRender.activeConfigs,
  currentProfile: state.quotation.currentProfile,
  copyingQuotation: state.quotation.copyQuotationData.copyingQuotation,
});

const reduxActions = {
  setCarAction: updateGeneralData,
  getCarDetailsFromLicenseAction: getCarDetailsFromLicense,
  getModelsAction: getModels,
  cleanCarDetailAction: cleanCarDetailsFromLicense,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: (car: CarItem) => void;
  loading: boolean;
  onLicenseError: (value: boolean) => void;
}

export default connector(CarDetailsForm);
